import type { Chart, Plugin } from "chart.js";

const wrapText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] => {
  const words = text.split(" ");
  const lines: string[] = [];
  let current = "";

  for (const word of words) {
    const testLine = current ? `${current} ${word}` : word;
    const { width } = ctx.measureText(testLine);

    if (width <= maxWidth) {
      current = testLine;
    } else {
      if (current) lines.push(current);
      current = word;
    }

    if (lines.length === 1) break; 
  }

  if (current && lines.length < 2) {
    lines.push(current);
  }

  return lines;
};

export const barRowLabelsPlugin: Plugin<"bar"> = {
  id: "barRowLabels",

  afterDatasetsDraw(chart: Chart<"bar">) {
    if (chart.config.type !== "bar") return;

    const { ctx } = chart;

    const labels = (chart.data.labels ?? []) as string[];
    const dataset = chart.data.datasets?.[0];
    if (!labels.length || !dataset || !dataset.data) return;

    const yScale = chart.scales?.y;
    if (!yScale) return;

    // Use the chart's left layout padding to determine label width
    const leftPadRaw = (chart.options.layout?.padding as any)?.left ?? 200;
    const leftPad =
      typeof leftPadRaw === "number" ? leftPadRaw : 200;

    const xStart = 12; // where labels begin
    const rightMarginInsideLabelArea = 12;
    const maxLabelWidth = Math.max(
      20,
      leftPad - xStart - rightMarginInsideLabelArea
    );

    ctx.save();
    ctx.font = "400 14px Inter, sans-serif";
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";

    const lineHeight = 16; 
    for (let i = 0; i < labels.length; i++) {
      const label = labels[i] ?? "";
      const raw = (dataset.data as any[])[i];
      if (raw == null) continue;

      const y = yScale.getPixelForTick(i);
      if (!Number.isFinite(y)) continue;

      const lines = wrapText(ctx, label, maxLabelWidth);

      // Center multi-line label around bar centerline
      const startY = lines.length === 1 ? y : y - lineHeight / 2;

      for (let li = 0; li < lines.length; li++) {
        ctx.fillText(lines[li], xStart, startY + li * lineHeight);
      }
    }

    ctx.restore();
  },
};
